import { type QueryClient, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useState } from 'react'
import { appPersister } from '@/lib/query-persist'
import { getMe, type Me } from './api'
import { authMeKey, isAuthMeKey } from './keys'
import { announceSignIn } from './session-channel'

export { authMeKey }

/**
 * How long the loader waits on an unanswered /auth/me before offering "Try
 * again". Past this the check is hung (a dead proxy, a stalled socket), and a
 * loader with no end is a page that never opens.
 */
export const SESSION_CHECK_WAIT_MS = 8000

/**
 * The user id one /auth/me in THIS page's life has confirmed, per query client.
 *
 * The cache is restored from disk, and what it holds is the last person's. Until
 * the server has answered once, a restored user is only a guess — after an
 * OAuth sign-in as someone else it is the wrong person, and drawing the page
 * from it shows A's data to B until the answer lands. So a restored user is
 * never handed out until an answer here names the same id (useAuth).
 */
const confirmedFor = new WeakMap<QueryClient, string>()

/**
 * Asks who is signed in, and forgets the last person's data if it is someone else.
 *
 * The cache is persisted across reloads, so what is on screen may be a previous
 * session's. When the answer names a DIFFERENT user than the one the cache was
 * filled for, every other query is dropped — inactive ones removed, the ones on
 * screen reset and refetched under the new session — and the disk copy removed.
 * A signed-out answer is left to the guard, which clears everything on its way
 * to /login. A signed-in answer for a new person is announced to the other tabs.
 */
export async function askWhoIsSignedIn(client: QueryClient): Promise<Me | null> {
  const before = client.getQueryData<Me | null>(authMeKey)
  const me = await getMe()
  if (before && me && before.id !== me.id) {
    const others = { predicate: (q: { queryKey: readonly unknown[] }) => !isAuthMeKey(q.queryKey) }
    client.removeQueries({ ...others, type: 'inactive' })
    void client.resetQueries({ ...others, type: 'active' }).catch(() => {})
    await appPersister.removeClient()
  }
  // A new person here (a sign-in, or someone else's session): the other tabs
  // hold the old answer in memory, so tell them to ask again.
  if (me && before?.id !== me.id) announceSignIn(me.id)
  if (me) confirmedFor.set(client, me.id)
  else confirmedFor.delete(client)
  return me
}

/**
 * Another tab signed `userId` in. Nothing to do when this tab already shows
 * that person; otherwise ask who is signed in now, which drops the last
 * person's data when it was someone else's (askWhoIsSignedIn).
 */
export function onSignInElsewhere(client: QueryClient, userId: string): Promise<void> {
  if (client.getQueryData<Me | null>(authMeKey)?.id === userId) return Promise.resolve()
  return refreshSession(client)
}

/**
 * Asks the server who is signed in and WAITS for the answer. Call it after a
 * sign-in, before navigating into the app.
 *
 * Not `invalidateQueries`: that refetches only queries someone is watching, and
 * on the auth pages nobody is — RequireAuth unmounted on its way here, leaving
 * its cached "signed out" behind. Invalidating only marked it stale, so the
 * guard remounted on that stale null and bounced straight back to /login; the
 * second attempt worked because the first one's background refetch had landed
 * by then (#197).
 *
 * A failed ask is swallowed: the guard has its own answer for "could not check".
 */
export async function refreshSession(client: QueryClient): Promise<void> {
  await client
    .fetchQuery({ queryKey: authMeKey, queryFn: () => askWhoIsSignedIn(client), staleTime: 0 })
    .catch(() => {})
}

/**
 * Reads the current user from the sealed session via GET /api/auth/me.
 *
 * THREE states, not two. "Signed out" and "we could not ask" are different
 * answers, and collapsing them is how a signed-in person gets shown a login
 * form: `getMe` returns null for 401/403 — the real signed-out answer — and
 * THROWS for anything else, which left `data` undefined and read as null.
 *
 * The retry matters for the same reason. A 401 is not an error here, so it can
 * never be retried; only a genuine failure to reach the endpoint is. With
 * `retry: false` a single blip signed the person out of the UI.
 */
export function useAuth() {
  const client = useQueryClient()
  const { data, isPending, isError, isFetching, fetchStatus, errorUpdateCount, refetch } = useQuery(
    {
      queryKey: authMeKey,
      queryFn: () => askWhoIsSignedIn(client),
      retry: 2,
      // Always stale: a restored session is shown at once and rechecked behind
      // it on every mount and focus, never trusted for the 30s default.
      staleTime: 0,
    },
  )
  const answered = data ?? null
  // A user this page has not heard the server name yet is a restored guess:
  // hold it back (loader) until the answer confirms it — however long that
  // takes. Correctness over speed: the wrong person's data is never drawn.
  const confirmed = answered !== null && confirmedFor.get(client) === answered.id
  const unconfirmed = answered !== null && !confirmed
  const user = confirmed ? answered : null
  // Once this query has ever errored, a refetch is a RECHECK, not a first load.
  // React Query puts an empty query back to `pending` on refetch, so without
  // this the retry screen replaced itself with a bare "Loading…" for the whole
  // backoff and then came back — a worse flicker than the inert button the
  // feedback below was added to fix.
  const everAnswered = errorUpdateCount > 0
  // Pending covers the retries too, so a blip reads as "still asking".
  // An unconfirmed restored user counts as "still asking" until the check fails.
  const waiting = (isPending && !everAnswered) || (unconfirmed && !isError)
  // "Still asking" has to end. Offline, React Query PAUSES the check instead of
  // failing it, so isError never fires; a hung request never answers at all.
  // Either way the loader would spin forever — so a paused check, or one past
  // SESSION_CHECK_WAIT_MS, becomes "could not check" (isUnknown). The user
  // stays withheld: stalling never draws an unconfirmed restored user.
  const paused = fetchStatus === 'paused'
  const [timedOut, setTimedOut] = useState(false)
  useEffect(() => {
    if (!waiting) {
      setTimedOut(false)
      return
    }
    if (timedOut) return
    const timer = setTimeout(() => setTimedOut(true), SESSION_CHECK_WAIT_MS)
    return () => clearTimeout(timer)
  }, [waiting, timedOut])
  const stalled = waiting && (paused || timedOut)
  // A new attempt gets a fresh wait: the loader again, then "Try again" again
  // if this one hangs too. A hung ask is cancelled first — on a query with no
  // data React Query hands a refetch the in-flight promise, so without the
  // cancel "Try again" would wait on the very request that hung.
  const recheck = useCallback(async () => {
    if (timedOut) await client.cancelQueries({ queryKey: authMeKey })
    setTimedOut(false)
    return refetch()
  }, [client, refetch, timedOut])
  return {
    user,
    isLoading: waiting && !stalled,
    isAuthenticated: user !== null,
    /**
     * True when the session could not be determined — NOT the same as signed
     * out, and NOT the same as "the last request failed".
     *
     * Both halves are load-bearing, and each was wrong once.
     *
     * The data half: React Query KEEPS the last good data across a failed
     * refetch, and with no staleTime every tab-back refetches — so `isError`
     * alone is true while a perfectly good user is in hand, and a consumer
     * checking it first would replace a working signed-in app with an error
     * screen on any blip. And `data === undefined`, not `user === null`,
     * because data is NULL after a genuine 401 — a real signed-out answer —
     * and UNDEFINED only when the question was never answered. Keying on null
     * takes the sign-in form away from a signed-out person mid-blip.
     *
     * The pending half keeps this true through a recheck, which React Query
     * reports as `pending` on an empty query.
     */
    isUnknown:
      ((isError || (isPending && everAnswered)) && data === undefined) ||
      (unconfirmed && isError) ||
      stalled,
    /**
     * A recheck is in flight — for disabling the button that started it.
     * `paused` counts: offline, the refetch is queued and will not run, and a
     * button that looks ready invites a click that does nothing. A fetch past
     * the wait does not: it is hung, and "Try again" starts a fresh one.
     */
    isRechecking: (isFetching && !timedOut) || paused,
    recheck,
  }
}
